/**
 * ExtractedDocumentsPanel - Display AI-extracted supporting document data
 * =========================================================================
 * 
 * Shows extracted terms from lease agreements, insurance policies, and loan documents.
 * Used in case detail pages to display key financial terms.
 * 
 * P2: Case Pipeline - Lease/Contract Document Parsing
 * @author ClaimSync Team
 * @created August 2025
 */

import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { FinancialValue } from './shared/FinancialValue';
import { formatCurrency } from '../utils/financialFormat';
import {
  FileText,
  Home,
  Shield,
  Landmark,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  DollarSign,
  Calendar,
  Percent,
  Eye,
  AlertCircle,
  Edit
} from 'lucide-react';

// Types
interface LeaseTerms {
  monthly_rent?: number;
  lease_start_date?: string;
  lease_end_date?: string;
  lease_term_months?: number;
  property_address?: string;
  landlord_name?: string;
  escalation?: {
    type: string;
    rate?: number;
    frequency: string;
  };
  cam_charges?: number;
  square_footage?: number;
}

interface InsuranceTerms {
  policy_number?: string;
  carrier_name?: string;
  bi_coverage_limit?: number;
  bi_waiting_period_days?: number;
  bi_period_of_indemnity_months?: number;
  bi_deductible?: number;
  policy_effective_date?: string;
  policy_expiration_date?: string;
  exclusions?: string[];
}

interface LoanTerms {
  monthly_payment?: number;
  interest_rate?: number;
  original_principal?: number;
  current_balance?: number;
  loan_maturity_date?: string;
  lender_name?: string;
  loan_type?: string;
  is_sba_loan?: boolean;
  ppp_loan?: boolean;
  eidl_loan?: boolean;
}

interface ExtractedDocument {
  document_id: string;
  case_id: string;
  document_type: 'lease_agreement' | 'insurance_policy' | 'loan_document' | 'contract';
  confidence: 'high' | 'medium' | 'low' | 'manual';
  confidence_score: number;
  needs_review: boolean;
  extracted_at: string;
  original_filename?: string;
  lease_terms?: LeaseTerms;
  insurance_terms?: InsuranceTerms;
  loan_terms?: LoanTerms;
  extraction_warnings?: string[];
  verified_by?: string;
  verified_at?: string;
}

interface DocumentSummary {
  case_id: string;
  total_documents: number;
  extracted_documents: number;
  pending_extraction: number;
  needs_review: number;
  by_type: Record<string, number>;
  documents: ExtractedDocument[];
}

interface ExtractedDocumentsPanelProps {
  caseId: string;
  compact?: boolean;
  onExtract?: () => void;
}

// Utility functions
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const formatPercent = (value?: number): string => {
  if (value === undefined || value === null) return 'N/A';
  // If stored as decimal (e.g., 0.065), convert to percentage
  const pct = value < 1 ? value * 100 : value;
  return `${pct.toFixed(2)}%`;
};

// Document type icons and colors
const DOC_CONFIG = {
  lease_agreement: {
    icon: Home,
    color: 'var(--color-text-secondary)',
    bg: 'var(--color-bg-elevated)',
    border: 'var(--color-border)',
    label: 'Lease Agreement'
  },
  insurance_policy: {
    icon: Shield,
    color: 'var(--color-text-secondary)',
    bg: 'var(--color-bg-elevated)',
    border: 'var(--color-border)',
    label: 'Insurance Policy'
  },
  loan_document: {
    icon: Landmark,
    color: 'var(--color-text-secondary)',
    bg: 'var(--color-bg-elevated)',
    border: 'var(--color-border)',
    label: 'Loan Document'
  },
  contract: {
    icon: FileText,
    color: 'var(--color-text-secondary)',
    bg: 'var(--color-bg-elevated)',
    border: 'var(--color-border)',
    label: 'Contract'
  }
};

// Confidence badge
const ConfidenceBadge: React.FC<{ confidence: string; score: number }> = ({ confidence, score }) => {
  const config: Record<string, { color: React.CSSProperties; label: string }> = {
    high: { color: { color: 'var(--color-verified)', backgroundColor: 'transparent' }, label: 'High' },
    medium: { color: { color: 'var(--color-text-muted)', backgroundColor: 'transparent' }, label: 'Medium' },
    low: { color: { color: 'var(--color-needs-human)', backgroundColor: 'transparent' }, label: 'Low' },
    manual: { color: { color: 'var(--color-verified)', backgroundColor: 'transparent' }, label: 'Verified' }
  };
  const cfg = config[confidence as keyof typeof config] || config.medium;

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[2px] text-xs font-medium" style={cfg.color}>
      {confidence === 'manual' ? <CheckCircle className="w-3 h-3" /> : null}
      {cfg.label} <span className="cs-figure" style={{ color: 'inherit' }}>({Math.round(score * 100)}%)</span>
    </span>
  );
};

// Lease Terms Display
const LeaseTermsDisplay: React.FC<{ terms: LeaseTerms }> = ({ terms }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
    <div>
      <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Monthly Rent</p>
      <p className="cs-figure-lg">
        <FinancialValue value={terms.monthly_rent} />
      </p>
    </div>
    <div>
      <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Lease Term</p>
      <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
        {formatDate(terms.lease_start_date)} - {formatDate(terms.lease_end_date)}
      </p>
    </div>
    {terms.escalation && (
      <div>
        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Escalation</p>
        <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          {terms.escalation.rate ? <span className="cs-figure" style={{ color: 'inherit' }}>{terms.escalation.rate}%</span> : terms.escalation.type} {terms.escalation.frequency}
        </p>
      </div>
    )}
    {terms.cam_charges && (
      <div>
        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>CAM Charges</p>
        <p className="cs-figure"><FinancialValue value={terms.cam_charges} />/mo</p>
      </div>
    )}
    {terms.property_address && (
      <div className="col-span-2">
        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Property</p>
        <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>{terms.property_address}</p>
      </div>
    )}
    {terms.landlord_name && (
      <div>
        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Landlord</p>
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{terms.landlord_name}</p>
      </div>
    )}
  </div>
);

// Insurance Terms Display
const InsuranceTermsDisplay: React.FC<{ terms: InsuranceTerms }> = ({ terms }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div>
        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>BI Coverage Limit</p>
        <p className="cs-figure-lg">
          <FinancialValue value={terms.bi_coverage_limit} />
        </p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>BI Waiting Period</p>
        <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          {terms.bi_waiting_period_days ? `${terms.bi_waiting_period_days} days` : 'N/A'}
        </p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Period of Indemnity</p>
        <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          {terms.bi_period_of_indemnity_months ? `${terms.bi_period_of_indemnity_months} months` : 'N/A'}
        </p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>BI Deductible</p>
        <p className="cs-figure"><FinancialValue value={terms.bi_deductible} /></p>
      </div>
      {terms.carrier_name && (
        <div>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Carrier</p>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{terms.carrier_name}</p>
        </div>
      )}
      {terms.policy_number && (
        <div>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Policy #</p>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{terms.policy_number}</p>
        </div>
      )}
    </div>
    {terms.exclusions && terms.exclusions.length > 0 && (
      <div className="p-3 rounded-[2px] border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
        <p className="text-xs font-semibold mb-1 inline-flex items-center gap-1" style={{ color: 'var(--color-needs-human)' }}><AlertTriangle size={12} /> Policy Exclusions</p>
        <ul className="text-sm space-y-0.5" style={{ color: 'var(--color-text-muted)' }}>
          {terms.exclusions.slice(0, 5).map((exc, i) => (
            <li key={i}>• {exc}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

// Loan Terms Display
const LoanTermsDisplay: React.FC<{ terms: LoanTerms }> = ({ terms }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div>
        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Monthly Payment</p>
        <p className="cs-figure-lg">
          <FinancialValue value={terms.monthly_payment} />
        </p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Interest Rate</p>
        <p className="cs-figure">{formatPercent(terms.interest_rate)}</p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Original Principal</p>
        <p className="cs-figure"><FinancialValue value={terms.original_principal} /></p>
      </div>
      {terms.current_balance && (
        <div>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Current Balance</p>
          <p className="cs-figure"><FinancialValue value={terms.current_balance} /></p>
        </div>
      )}
      {terms.loan_maturity_date && (
        <div>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Maturity Date</p>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{formatDate(terms.loan_maturity_date)}</p>
        </div>
      )}
      {terms.lender_name && (
        <div>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Lender</p>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{terms.lender_name}</p>
        </div>
      )}
    </div>
    {(terms.is_sba_loan || terms.ppp_loan || terms.eidl_loan) && (
      <div className="flex flex-wrap gap-2">
        {terms.is_sba_loan && (
          <span className="px-2 py-1 text-xs font-medium rounded-[2px]" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>SBA Loan</span>
        )}
        {terms.ppp_loan && (
          <span className="px-2 py-1 text-xs font-medium rounded-[2px]" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>PPP Loan</span>
        )}
        {terms.eidl_loan && (
          <span className="px-2 py-1 text-xs font-medium rounded-[2px]" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>EIDL Loan</span>
        )}
      </div>
    )}
  </div>
);

// Single Document Card
const DocumentCard: React.FC<{
  doc: ExtractedDocument;
  expanded: boolean;
  onToggle: () => void;
}> = ({ doc, expanded, onToggle }) => {
  const config = DOC_CONFIG[doc.document_type] || DOC_CONFIG.contract;
  const Icon = config.icon;
  
  const getTermsDisplay = () => {
    switch (doc.document_type) {
      case 'lease_agreement':
        return doc.lease_terms ? <LeaseTermsDisplay terms={doc.lease_terms} /> : null;
      case 'insurance_policy':
        return doc.insurance_terms ? <InsuranceTermsDisplay terms={doc.insurance_terms} /> : null;
      case 'loan_document':
        return doc.loan_terms ? <LoanTermsDisplay terms={doc.loan_terms} /> : null;
      default:
        return null;
    }
  };
  
  const getKeyValue = () => {
    switch (doc.document_type) {
      case 'lease_agreement':
        return doc.lease_terms?.monthly_rent ? formatCurrency(doc.lease_terms.monthly_rent) + '/mo' : null;
      case 'insurance_policy':
        return doc.insurance_terms?.bi_coverage_limit ? formatCurrency(doc.insurance_terms.bi_coverage_limit) : null;
      case 'loan_document':
        return doc.loan_terms?.monthly_payment ? formatCurrency(doc.loan_terms.monthly_payment) + '/mo' : null;
      default:
        return null;
    }
  };

  return (
    <div className="rounded-[2px] border overflow-hidden" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-white/50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-[2px] flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <Icon className="w-5 h-5" style={{ color: config.color }} />
          </div>
          <div className="text-left min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{config.label}</span>
              <ConfidenceBadge confidence={doc.confidence} score={doc.confidence_score} />
              {doc.needs_review && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[2px] text-xs font-medium" style={{ color: 'var(--color-needs-human)' }}>
                  <AlertCircle className="w-3 h-3" />
                  Needs Review
                </span>
              )}
            </div>
            <p className="text-sm truncate" style={{ color: 'var(--color-text-muted)' }}>
              {doc.original_filename || 'Document'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0 pl-4">
          {getKeyValue() && (
            <span className="cs-figure" style={{ color: 'var(--color-text-secondary)' }}>{getKeyValue()}</span>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
          ) : (
            <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 pt-0 border-t border-inherit">
          {getTermsDisplay()}

          {/* Warnings */}
          {doc.extraction_warnings && doc.extraction_warnings.length > 0 && (
            <div className="mt-4 p-3 rounded-[2px] border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-needs-human)' }}>Extraction Notes</p>
              <ul className="text-sm space-y-0.5" style={{ color: 'var(--color-text-muted)' }}>
                {doc.extraction_warnings.map((warning, i) => (
                  <li key={i}>• {warning}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Verification status */}
          {doc.verified_by && (
            <p className="mt-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              ✓ Verified by {doc.verified_by} on {formatDate(doc.verified_at)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
export default function ExtractedDocumentsPanel({
  caseId,
  compact = false,
  onExtract
}: ExtractedDocumentsPanelProps) {
  const [summary, setSummary] = useState<DocumentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);

  const fetchDocuments = async () => {
    if (!caseId) return;
    setLoading(true);
    setError(null);
    
    try {
      const data = await api.get<DocumentSummary>(`/api/v1/supporting-documents/case/${caseId}`);
      setSummary(data);
      // Auto-expand first document if only one
      if (data.documents.length === 1) {
        setExpandedDoc(data.documents[0].document_id);
      }
    } catch (err: any) {
      console.error('Error fetching extracted documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleExtractAll = async () => {
    setExtracting(true);
    try {
      await api.post(`/api/v1/supporting-documents/case/${caseId}/extract-all`, {});
      await fetchDocuments();
      onExtract?.();
    } catch (err: any) {
      setError(err.message || 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [caseId]);

  if (loading) {
    return (
      <div className="p-4 rounded-[2px] border" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading supporting documents...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-[2px] border" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center gap-2" style={{ color: 'var(--color-blocker)' }}>
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{error}</span>
        </div>
        <button
          onClick={fetchDocuments}
          className="mt-2 text-xs flex items-center gap-1"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <RefreshCw className="w-3 h-3" />
          Retry
        </button>
      </div>
    );
  }

  if (!summary || summary.documents.length === 0) {
    return (
      <div className="p-6 rounded-[2px] border text-center" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}>
        <FileText className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--color-text-ghost)' }} />
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No extracted supporting documents</p>
        {summary && summary.pending_extraction > 0 && (
          <button
            onClick={handleExtractAll}
            disabled={extracting}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm rounded-[2px] disabled:opacity-50"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {extracting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            Extract {summary.pending_extraction} Documents
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="cs-label" style={{ color: 'var(--color-text-muted)' }}>Supporting Documents</h3>
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            <span>{summary.extracted_documents} extracted</span>
            {summary.needs_review > 0 && (
              <span>• {summary.needs_review} need review</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {summary.pending_extraction > 0 && (
            <button
              onClick={handleExtractAll}
              disabled={extracting}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-[2px] disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              {extracting ? (
                <RefreshCw className="w-3 h-3 animate-spin" />
              ) : (
                <Eye className="w-3 h-3" />
              )}
              Extract ({summary.pending_extraction})
            </button>
          )}
          <button
            onClick={fetchDocuments}
            className="p-1.5"
            style={{ color: 'var(--color-text-dim)' }}
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Document Cards */}
      <div className="space-y-3">
        {summary.documents.map((doc) => (
          <DocumentCard
            key={doc.document_id}
            doc={doc}
            expanded={expandedDoc === doc.document_id}
            onToggle={() => setExpandedDoc(
              expandedDoc === doc.document_id ? null : doc.document_id
            )}
          />
        ))}
      </div>
    </div>
  );
}
